import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import { useT } from "@/i18n";
import { useRealtime } from "@/context/RealtimeContext";
import UserAvatar from "@/components/UserAvatar";
import {
  LayoutDashboard, Users, CalendarDays, ListChecks, MessageSquare, CalendarClock,
  Clock, HeartPulse, Bell, Sun, Moon, Languages, LogOut, Menu, X, Check, Building2, ChevronsUpDown,
  BedDouble, GraduationCap, HeartHandshake, CalendarRange, Receipt, BarChart3, Sparkles, Inbox as InboxIcon, KanbanSquare, ClipboardCheck, ClipboardList, Settings as SettingsIcon,
} from "lucide-react";
import ClockWidget from "@/components/ClockWidget";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, roles: ["admin", "supervisor"] },
  { to: "/employees", key: "employees", icon: Users, roles: ["admin", "supervisor"] },
  { to: "/residents", key: "residents", icon: BedDouble, roles: ["admin", "supervisor"] },
  { to: "/schedule", key: "schedule", icon: CalendarDays, roles: ["admin", "supervisor"] },
  { to: "/my-schedule", key: "mySchedule", icon: CalendarDays, roles: ["employee"] },
  { to: "/calendar", key: "calendar", icon: CalendarRange, roles: ["admin", "supervisor", "employee"] },
  { to: "/tickets", key: "tickets", icon: KanbanSquare, roles: ["admin", "supervisor"] },
  { to: "/presence", key: "presence", icon: ClipboardCheck, roles: ["admin", "supervisor"] },
  { to: "/my-tickets", key: "myTickets", icon: KanbanSquare, roles: ["employee"] },
  { to: "/care", key: "residentCare", icon: HeartHandshake, roles: ["employee"] },
  { to: "/assistant", key: "assistant", icon: Sparkles, roles: ["admin", "supervisor", "employee"] },
  { to: "/inbox", key: "inbox", icon: InboxIcon, roles: ["admin", "supervisor"] },
  { to: "/training", key: "training", icon: GraduationCap, roles: ["admin", "supervisor", "employee"] },
  { to: "/chat", key: "chat", icon: MessageSquare, roles: ["admin", "supervisor", "employee"] },
  { to: "/shift-reports", key: "shiftReports", icon: ClipboardList, roles: ["admin", "supervisor"] },
  { to: "/billing", key: "billing", icon: Receipt, roles: ["admin"] },
];

function NotificationBell() {
  const { notifications, unreadCount, markAllRead, markRead } = useRealtime();
  const { t } = useT();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative w-9 h-9 rounded-lg hover:bg-accent flex items-center justify-center transition-colors" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
              {unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">{t("notifications")}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">{t("markAllRead")}</button>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {notifications.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">{t("noNotifications")}</p>}
          {notifications.slice(0, 20).map((n) => (
            <div key={n.id} onClick={() => markRead(n.id)}
              className={`px-3 py-2.5 border-b border-border last:border-0 cursor-pointer hover:bg-accent/60 ${!n.read ? "bg-primary/5" : ""}`}
              data-testid="notification-item">
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-muted-foreground">{n.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout, theme, toggleTheme } = useAuth();
  const { role, isSuperAdmin, centers, activeCenter, switchCenter } = useCenter();
  const { t, lang, toggleLang } = useT();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const effectiveRole = isSuperAdmin ? "admin" : role;
  const items = NAV.filter((n) => n.roles.includes(effectiveRole));

  const CenterSwitcher = ({ inSidebar }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="center-switcher"
          className={inSidebar
            ? "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            : "flex items-center gap-2 h-9 px-3 rounded-lg border border-border hover:bg-accent transition-colors w-full min-w-0 max-w-[220px]"}>
          <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${activeCenter?.color || "#3B82F6"}${inSidebar ? "" : "22"}` }}>
            <Building2 className={`w-4 h-4 ${inSidebar ? "text-white" : ""}`} style={inSidebar ? {} : { color: activeCenter?.color }} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className={`text-sm font-semibold truncate ${inSidebar ? "text-sidebar-foreground" : ""}`}>{activeCenter?.name || "—"}</p>
            {inSidebar && <p className="text-[11px] text-sidebar-muted capitalize">{t(effectiveRole)}</p>}
          </div>
          <ChevronsUpDown className={`w-4 h-4 shrink-0 ${inSidebar ? "text-sidebar-muted" : "text-muted-foreground"}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Centers</DropdownMenuLabel>
        {(centers || []).map((c) => (
          <DropdownMenuItem key={c.id} onClick={() => { switchCenter(c.id); navigate("/"); }} data-testid={`center-option-${c.id}`}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center mr-2" style={{ backgroundColor: `${c.color}22` }}>
              <Building2 className="w-3.5 h-3.5" style={{ color: c.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{c.name}</p>
              <p className="text-[11px] text-muted-foreground capitalize">{t(c.my_role)}</p>
            </div>
            {c.id === activeCenter?.id && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        {isSuperAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/centers")} data-testid="manage-centers">
              <Building2 className="w-4 h-4 mr-2" /> {t("manageCenters")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 h-16 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <HeartPulse className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-display font-extrabold leading-none text-sidebar-foreground">{t("appName")}</p>
          <p className="text-sidebar-muted text-[11px] mt-0.5">{t("tagline")}</p>
        </div>
      </div>
      <div className="px-3 pb-2"><CenterSwitcher inSidebar /></div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {items.map(({ to, key, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={() => setMobileOpen(false)}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-sidebar-active text-white" : "text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/5"
              }`}>
            <Icon className="w-[18px] h-[18px]" /> {t(key)}
          </NavLink>
        ))}
        {isSuperAdmin && (
          <NavLink to="/centers" onClick={() => setMobileOpen(false)} data-testid="nav-centers"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-sidebar-active text-white" : "text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/5"
              }`}>
            <Building2 className="w-[18px] h-[18px]" /> {t("centers")}
          </NavLink>
        )}
        <NavLink to="/settings" onClick={() => setMobileOpen(false)} data-testid="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-sidebar-active text-white" : "text-sidebar-muted hover:text-sidebar-foreground hover:bg-white/5"
            }`}>
          <SettingsIcon className="w-[18px] h-[18px]" /> {t("settings")}
        </NavLink>
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <UserAvatar name={user.name} src={user.profile_picture} size={36} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{user.name}</p>
            <p className="text-[11px] text-sidebar-muted capitalize">{isSuperAdmin ? t("owner") : t(effectiveRole)}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-sidebar sidebar-noise shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 flex flex-col bg-sidebar sidebar-noise">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-sidebar-muted" data-testid="close-mobile-nav"><X className="w-5 h-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 shrink-0 border-b border-border bg-card/80 backdrop-blur flex items-center gap-2 px-4 sm:px-6">
          <button className="lg:hidden w-9 h-9 rounded-lg hover:bg-accent flex items-center justify-center" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav">
            <Menu className="w-5 h-5" />
          </button>
          <div className="lg:hidden flex-1 min-w-0"><CenterSwitcher /></div>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            <button onClick={toggleLang} className="h-9 px-2.5 rounded-lg hover:bg-accent flex items-center gap-1.5 text-sm font-medium transition-colors" data-testid="lang-toggle">
              <Languages className="w-4 h-4" /> {lang.toUpperCase()}
            </button>
            <button onClick={toggleTheme} className="w-9 h-9 rounded-lg hover:bg-accent flex items-center justify-center transition-colors" data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1" data-testid="user-menu"><UserAvatar name={user.name} src={user.profile_picture} size={36} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> {t("signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
          {children}
        </main>
      </div>
      <ClockWidget />
    </div>
  );
}
